import * as Sentry from '@sentry/react';
import { format, parse } from 'date-fns';
import { find, orderBy } from 'lodash';
import Pluralize from 'pluralize';
import React, { useMemo } from 'react';

import {
    GroupedByResident,
    OperationsBranchStatsDataset,
    OperationsBranchStatsResponse,
    OperationsDocumentationRateStatsDataset,
    ResidentDataset,
    ResidentWithDetails,
    SectionDetail,
} from 'types/operationsDashboard';

import BranchDocumentationRateStatsCards from '../components/Tables/BranchDocumentationRateStatsCards';
import BranchStatsTable, { DataRow } from '../components/Tables/BranchStatsTable';
import BranchStatsTableWithCollapsibleRows from '../components/Tables/BranchStatsTableWithCollapsibleRows';
import {
    DATATYPES_WITH_COLLAPSIBLE_ROWS,
    DETAILS_COLUMNS_PROPS,
    DYNAMIC_COLUMNS,
    EXTRA_COLLAPSIBLE_ROW_COLUMNS,
    TOTAL_ROW_LABEL,
    isChangeOfConditionDetail,
} from '../components/Tables/constants';

import { BRANCH_SECTIONS_MAP, Section, StandardDataset } from '../constants';
import { calculatePercentage } from '../helpers';

const getDatasets = (data: OperationsBranchStatsDataset[], branchId: number, startDate?: string) => {
    let selectedDate = startDate;

    const branchDatasets = data.filter((dataset) => dataset.branchId === branchId);

    if (!startDate) {
        const dates = Array.from(
            new Set<Date>(branchDatasets.map((dataset) => parse(dataset.startDate, 'yyyy-MM-dd', new Date())))
        );

        dates.sort((a, b) => a.getTime() - b.getTime());

        if (dates.length) selectedDate = format(dates[dates.length - 2], 'yyyy-MM-dd');
    }

    return branchDatasets.find((dataset) => dataset.startDate === selectedDate);
};

const groupByResident = (datasets: ResidentDataset[], currentDataType: Section): ResidentWithDetails[] => {
    const grouped: GroupedByResident = {};

    datasets.forEach((dataset) => {
        const { id } = dataset.resident;

        if (!grouped[id]) {
            grouped[id] = {
                resident: dataset.resident,
                details: [],
            };
        }

        const detailKeys = DETAILS_COLUMNS_PROPS[currentDataType]!;
        const detailObject = detailKeys.reduce((acc, key) => {
            acc[key] = dataset[key];
            return acc;
        }, {} as SectionDetail);

        grouped[id].details.push(detailObject);
    });

    return Object.values(grouped);
};

const processedTableBranchData = (
    data: OperationsBranchStatsResponse | undefined,
    dataType: Section,
    branchId: string,
    startDate: string | undefined
) => {
    if (!data) {
        return null;
    }

    const sectionData = find(data.sections, (o) => o.section === dataType)!;
    // TODO: remove default value when the backend is sending us all 8 days worth of data
    const datasets = getDatasets(sectionData.datasets, Number(branchId), startDate)! ?? { datasets: [] };

    if (dataType === 'DOCUMENTATION_RATE') {
        type DocumentationShift = { shiftName: string; incompleteTasks: number };
        type DocumentationDataset = OperationsDocumentationRateStatsDataset & { shifts: DocumentationShift[] };
        type DocumentationStats = { [key: string]: DocumentationDataset };

        const { assistedLivingShifts, memoryCareShifts, enhancedIndependentLivingCareShifts } =
            datasets as DocumentationStats;

        return {
            assistedLivingShifts: {
                completionPercentage: calculatePercentage(
                    assistedLivingShifts?.tasksCompleteCount ?? 0,
                    assistedLivingShifts?.totalTasksCount ?? 0
                ),
                shifts: assistedLivingShifts?.shifts ?? [],
            },
            memoryCareShifts: {
                completionPercentage: calculatePercentage(
                    memoryCareShifts?.tasksCompleteCount ?? 0,
                    memoryCareShifts?.totalTasksCount ?? 0
                ),
                shifts: memoryCareShifts?.shifts ?? [],
            },
            enhancedIndependentLivingCareShifts: enhancedIndependentLivingCareShifts
                ? {
                      completionPercentage: calculatePercentage(
                          enhancedIndependentLivingCareShifts?.tasksCompleteCount ?? 0,
                          enhancedIndependentLivingCareShifts?.totalTasksCount ?? 0
                      ),
                      shifts: enhancedIndependentLivingCareShifts?.shifts ?? [],
                  }
                : undefined,
            rows: [],
            simpleRows: [],
            dynamicColumns: [],
        };
    }

    const hasCollapsibleRows = DATATYPES_WITH_COLLAPSIBLE_ROWS.includes(dataType);

    const residentDatasets = datasets.datasets as ResidentDataset[];
    const datasetsGroupedByResident: ResidentWithDetails[] = hasCollapsibleRows
        ? groupByResident(residentDatasets, dataType)
        : [];

    const simpleDatasets = hasCollapsibleRows ? [] : (datasets.datasets as ResidentDataset[]);

    const totalLabel = TOTAL_ROW_LABEL[dataType];

    const getTotalRowLabel = (currentDataset: ResidentWithDetails, currentDataType: Section) => {
        if (currentDataType === 'UNSCHEDULED_CARE') {
            const label = currentDataset.details.length > 1 ? `${totalLabel}s` : totalLabel;

            return `${currentDataset.details.length} ${label}`;
        }

        return totalLabel
            ? `${currentDataset.details.length} ${Pluralize(totalLabel, currentDataset.details.length)}`
            : '';
    };

    const extraCollapsibleRowCols = EXTRA_COLLAPSIBLE_ROW_COLUMNS[dataType];

    const getExtraCollapsibleRowCols = (currentDataset: ResidentWithDetails) =>
        extraCollapsibleRowCols
            ? extraCollapsibleRowCols.reduce((acc, key) => {
                  acc[key] = currentDataset.details[0][key];

                  if (dataType === 'FALLS' && key === 'last30FallCount') {
                      acc[key] = currentDataset.details.some((item) => (item[key] as number) >= 2) ? 'Yes' : 'No';
                  }

                  return acc;
              }, {})
            : {};

    const createRowFromDataset = (currentDataset: ResidentWithDetails, currentDataType: Section) => ({
        id: `${currentDataset.resident.id}`,
        residentName: currentDataset.resident.name,
        residentRoomNumber: currentDataset.resident.roomNumber,
        details: currentDataset.details,
        ...(TOTAL_ROW_LABEL[currentDataType] ? { total: getTotalRowLabel(currentDataset, currentDataType) } : {}),
        ...(extraCollapsibleRowCols ? getExtraCollapsibleRowCols(currentDataset) : {}),
    });

    const sortedDatasetsGroupedByResident = datasetsGroupedByResident.sort(
        (a, b) => b.details.length - a.details.length
    );

    // Sorts the datasets by the new tag if the data type is 'CHANGE_OF_CONDITION'.
    // For other data types, the datasets are sorted by the resident.
    // If a dataset has a row that is new, it is placed at the top.
    const sortedDatasetsByNewTag =
        dataType !== 'CHANGE_OF_CONDITION'
            ? sortedDatasetsGroupedByResident
            : sortedDatasetsGroupedByResident.sort((a) =>
                  a.details.some((detail) => isChangeOfConditionDetail(detail) && detail.isNewEvent) ? -1 : 1
              );

    const rows =
        sortedDatasetsByNewTag.length > 0 && hasCollapsibleRows
            ? sortedDatasetsByNewTag.map((dataset) => createRowFromDataset(dataset, dataType))
            : [];

    const getDatasetId = (dataset: ResidentDataset) => {
        // Concatinating the date with the resident id because for these
        // data types, there are multiple lines for the same resedents.
        if (dataType === 'OVERDUE_ASSESSMENT') {
            return `${dataset.resident.id}-${dataset.overdueDate}`;
        }

        if (dataType === 'FALL_DOCUMENTATION_INCOMPLETE') {
            return `${dataset.resident.id}-${dataset.fallDateTime}`;
        }

        if (dataType === 'HOSPITAL_TRANSFER') {
            return `${dataset.resident.id}-${dataset.date}`;
        }

        Sentry.captureException(`New datatype was added - ${dataType}. Please, add it to the getDatasetId function.`);
        return `${dataset.resident.id}`;
    };

    const simpleRows =
        simpleDatasets.length > 0 && !hasCollapsibleRows
            ? simpleDatasets.map((dataset: ResidentDataset) => ({
                  ...dataset,
                  id: getDatasetId(dataset),
                  residentName: dataset.resident.name,
                  residentRoomNumber: dataset.resident.roomNumber,
              }))
            : [];

    return {
        rows,
        simpleRows,
        dynamicColumns: DYNAMIC_COLUMNS[dataType],
    };
};

const processedChartBranchData = (data: OperationsBranchStatsResponse | undefined, dataType: Section) => {
    if (!data) {
        return null;
    }

    const sectionDatasets = find(data.sections, (o) => o.section === dataType)!;

    const { chartValue, chartValue2 } = BRANCH_SECTIONS_MAP[dataType];

    let dataset: StandardDataset[];
    let dataset2: StandardDataset[] | undefined;

    if (dataType === 'DOCUMENTATION_RATE') {
        dataset = sectionDatasets.datasets.map((item) => ({
            startDate: item.startDate,
            value: calculatePercentage(item[chartValue] as number, item[chartValue2] as number),
        }));
    } else {
        dataset = sectionDatasets.datasets.map((item) => ({
            startDate: item.startDate,
            value: item[chartValue] as number,
        }));

        dataset2 = chartValue2
            ? sectionDatasets.datasets.map((item) => ({
                  startDate: item.startDate,
                  value: item[chartValue2] as number,
              }))
            : [];
    }

    const orderedDatasets = orderBy(dataset, 'startDate', 'asc');

    const orderedDatasets2 = orderBy(dataset2, 'startDate', 'asc');

    return {
        data: orderedDatasets,
        data2: orderedDatasets2.length > 0 ? orderedDatasets2 : undefined,
        section: dataType,
    };
};

const getStartDateOptions = (data: OperationsBranchStatsResponse | undefined, dataType: Section) => {
    if (!data) {
        return null;
    }

    const sectionData = find(data.sections, (o) => o.section === dataType)!;

    return sectionData.datasets
        .map((dataset) => dataset.startDate)
        .reverse()
        .slice(0, -1);
};

const getOverallStat = (data: OperationsBranchStatsResponse | undefined, dataType: Section) => {
    if (!data) {
        return null;
    }

    const branchDatasets = find(data.sections, (o) => o.section === dataType)!.datasets;
    if (!branchDatasets.length) return null;

    const branchDataset = branchDatasets[branchDatasets.length - 2];
    const sectionsMap = BRANCH_SECTIONS_MAP[dataType];

    const chartValueAmount = (branchDataset[sectionsMap.chartValue] as number) ?? 0;
    const chartValue2Amount = (branchDataset[sectionsMap.chartValue2] as number) ?? 0;

    if (dataType === 'CHANGE_OF_CONDITION') {
        return chartValueAmount + chartValue2Amount;
    }

    if (dataType === 'DOCUMENTATION_RATE') {
        return `${calculatePercentage(chartValueAmount, chartValue2Amount)}%`;
    }

    return chartValueAmount;
};

export const useBranchDataItems = (
    data: OperationsBranchStatsResponse | undefined,
    dataType: Section,
    branchId: string,
    careType: string,
    startDate: string | undefined
) => {
    //  temporary hack to handle Branch level not having the same sections as the other levels
    const isValidSection = Object.keys(BRANCH_SECTIONS_MAP).includes(dataType);

    const ChartComponent = BRANCH_SECTIONS_MAP[dataType]?.ChartComponent;

    const chartProps = useMemo(
        () => (isValidSection ? processedChartBranchData(data, dataType) : null),
        [JSON.stringify(data), dataType]
    );

    const tableProps = useMemo(
        () => (isValidSection ? processedTableBranchData(data, dataType, branchId, startDate) : null),
        [JSON.stringify(data), dataType, branchId, startDate]
    );

    const dateOptions = useMemo(
        () => (isValidSection ? getStartDateOptions(data, dataType) : null),
        [JSON.stringify(data), dataType]
    );

    const branchStat = useMemo(
        () => (isValidSection ? getOverallStat(data, dataType) : null),
        [JSON.stringify(data), dataType]
    );

    if (!data || chartProps === null || tableProps === null) {
        return {};
    }

    const hasCollapsibleRows = DATATYPES_WITH_COLLAPSIBLE_ROWS.includes(dataType);

    // not super happy with this different approach from overall. Will revisit
    let renderTableComponent: (renderDateSelector: () => JSX.Element | null) => JSX.Element;

    if (dataType === 'DOCUMENTATION_RATE') {
        renderTableComponent = (renderDateSelector) => (
            <BranchDocumentationRateStatsCards
                renderDateSelector={renderDateSelector}
                selectedCareType={careType}
                assistedLivingShifts={tableProps.assistedLivingShifts!}
                memoryCareShifts={tableProps.memoryCareShifts!}
                enhancedIndependentLivingCareShifts={tableProps.enhancedIndependentLivingCareShifts}
            />
        );
    } else {
        renderTableComponent = hasCollapsibleRows
            ? (renderDateSelector) => (
                  <BranchStatsTableWithCollapsibleRows
                      rows={tableProps.rows}
                      dynamicColumns={tableProps.dynamicColumns}
                      dataType={dataType}
                      renderDateSelector={renderDateSelector}
                  />
              )
            : (renderDateSelector) => (
                  <BranchStatsTable
                      rows={tableProps.simpleRows as DataRow[]}
                      dynamicColumns={tableProps.dynamicColumns}
                      renderDateSelector={renderDateSelector}
                  />
              );
    }

    return {
        BranchChartComponent: ChartComponent,
        branchChartProps: chartProps,
        branchDateOptions: dateOptions,
        renderTableComponent,
        branchStat,
    };
};
